/**
 * UATFeedbackButton.jsx — Floating UAT feedback trigger.
 *
 * Renders a fixed bottom-right button only when UAT is explicitly enabled
 * for the current tenant/institution/environment.
 *
 * On mount it calls GET /api/uat/config. If uat_enabled = false (or the
 * call fails), this component renders nothing — ISB and VCE tenants are
 * never affected.
 *
 * Provider availability is checked separately: the modal shows the voice
 * tab only when voice_provider_status = AVAILABLE. Text feedback is
 * always available regardless of voice provider state.
 */

import React, { useEffect, useState, useCallback } from "react";
import { MessageSquarePlus, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { UATFeedbackModal } from "./UATFeedbackModal";

const API = process.env.REACT_APP_BACKEND_URL;

export function UATFeedbackButton() {
  const { user } = useAuth();
  const [uatConfig, setUatConfig] = useState(null); // null = loading / not enabled
  const [open, setOpen] = useState(false);
  const token = localStorage.getItem("academiaos_token");

  const checkConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/uat/config?environment=UAT`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.uat_enabled) {
          setUatConfig(data);
        }
        // If not enabled, uatConfig stays null and we render nothing
      }
    } catch {
      // Silently ignore — UAT feedback failure must never affect the host app
    }
  }, [token]);

  useEffect(() => {
    if (user) checkConfig();
  }, [user, checkConfig]);

  // Don't render anything if UAT is not enabled for this tenant
  if (!uatConfig) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="uat-feedback-floating-btn"
        title="Report UAT Feedback"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg border border-primary/20 bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 active:scale-95 transition-all"
        style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
      >
        <MessageSquarePlus className="h-4 w-4" />
        Report Feedback
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          data-testid="uat-feedback-overlay"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Modal panel */}
          <div
            className="relative w-full sm:max-w-xl max-h-[90dvh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-border bg-background shadow-2xl"
            data-testid="uat-feedback-modal"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-background z-10">
              <div>
                <h2 className="text-sm font-bold text-foreground">Report UAT Feedback</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Context is auto-captured. Just describe what happened.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="uat-modal-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-5 py-5">
              <UATFeedbackModal
                onClose={() => setOpen(false)}
                uatConfig={uatConfig}
                user={user}
                token={token}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
